/**
 * Phone parsing for the SMS channel — the browser-side twin of `backend/phone.py`.
 *
 * The backend normalises with Google's `phonenumbers`; this uses
 * `libphonenumber-js` built from the same metadata, so a number this module
 * accepts is a number `normalize_phone` accepts. That parity is the point: the
 * agent finds out the number is unusable while typing it, not after the
 * invitation row already exists and the SMS came back `skipped`.
 *
 * `/max` metadata is imported deliberately. The default (`min`) build validates
 * by length alone, which would wave through numbers Twilio rejects; `/max`
 * carries the per-type patterns that make `isValid()` and `getNumberType()`
 * mean what they say.
 */
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js/max";

/** Matches `DEFAULT_REGION` in backend/phone.py. */
export const DEFAULT_COUNTRY: CountryCode = "US";

export interface CountryOption {
  value: CountryCode;
  /** "🇺🇸 United States +1" — for the dropdown list. */
  label: string;
  /** "🇺🇸 +1" — for the collapsed trigger, which has no room for the name. */
  short: string;
  callingCode: string;
}

/** ISO 3166-1 alpha-2 → regional-indicator flag emoji. */
function flag(country: string): string {
  return String.fromCodePoint(
    ...country.split("").map((c) => 0x1f1a5 + c.charCodeAt(0)),
  );
}

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();

/**
 * Every dialable country, alphabetical, with the default market pinned first.
 *
 * Built from the library's own country list rather than a hand-kept table so it
 * cannot drift out of step with the metadata that validates the number.
 */
export const COUNTRY_OPTIONS: CountryOption[] = (() => {
  const options = getCountries().map((value) => {
    const callingCode = `+${getCountryCallingCode(value)}`;
    const name = regionNames?.of(value) ?? value;
    return { value, callingCode, label: `${flag(value)}  ${name}  ${callingCode}`, short: `${flag(value)} ${callingCode}` };
  });
  options.sort((a, b) => a.label.localeCompare(b.label));
  const defaultIndex = options.findIndex((o) => o.value === DEFAULT_COUNTRY);
  if (defaultIndex > 0) options.unshift(options.splice(defaultIndex, 1)[0]);
  return options;
})();

/**
 * E.164 (`+14155550134`) for a valid number, otherwise null.
 *
 * A leading `+` wins over `country`: someone who pastes a full international
 * number means that number, whatever the dropdown happens to say.
 */
export function toE164(raw: string, country: CountryCode = DEFAULT_COUNTRY): string | null {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return null;
  const parsed = parsePhoneNumberFromString(trimmed, trimmed.startsWith("+") ? undefined : country);
  return parsed?.isValid() ? parsed.number : null;
}

export function isValidPhone(raw: string, country: CountryCode = DEFAULT_COUNTRY): boolean {
  return toE164(raw, country) !== null;
}

/**
 * Split a stored E.164 back into the pair `PhoneField` edits.
 *
 * The inverse of `toE164`, and the reason it exists: what we persist is
 * `+923120287142`, but a country picker plus a national number is what a person
 * can read and correct. Without this, a saved number reloads as an opaque E.164
 * string sitting under whatever country the form happened to default to.
 *
 * Returns null when the string is not a valid number, so callers can leave a
 * half-typed value alone rather than blanking it.
 */
export function fromE164(e164: string): { country: CountryCode; national: string } | null {
  const parsed = parsePhoneNumberFromString((e164 ?? "").trim());
  if (!parsed?.isValid() || !parsed.country) return null;
  return { country: parsed.country, national: parsed.formatNational() };
}

/** Spaced international rendering — `+92 312 3456789`. Falls back to E.164. */
export function formatInternational(e164: string): string {
  return parsePhoneNumberFromString(e164)?.formatInternational() ?? e164;
}

/**
 * True when the carrier type cannot receive SMS.
 *
 * Only `FIXED_LINE` counts. US numbers overwhelmingly report
 * `FIXED_LINE_OR_MOBILE` because the NANP does not partition ranges by type, so
 * treating an ambiguous answer as a landline would reject most of the country.
 * Callers should warn on this, never block: type data is a best guess.
 */
export function isLikelyLandline(raw: string, country: CountryCode = DEFAULT_COUNTRY): boolean {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return false;
  const parsed = parsePhoneNumberFromString(trimmed, trimmed.startsWith("+") ? undefined : country);
  return parsed?.isValid() === true && parsed.getType() === "FIXED_LINE";
}

/**
 * Re-format while typing, so the field shows `(415) 555-0134` as it fills in.
 *
 * Skipped when the input got shorter: `AsYouType` would re-insert the very
 * separator the user just backspaced over, making the field impossible to edit.
 */
export function formatAsYouType(next: string, previous: string, country: CountryCode = DEFAULT_COUNTRY): string {
  if (next.length < previous.length) return next;
  if (next.trim().startsWith("+")) return new AsYouType().input(next);
  return new AsYouType(country).input(next);
}
